import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertShippingLocation =
  Database['public']['Tables']['shipping_locations']['Insert']
export type UpdateShippingLocation =
  Database['public']['Tables']['shipping_locations']['Update']
export type ShippingLocation_ =
  Database['public']['Tables']['shipping_locations']['Row']

export const useShippingLocationsApi = ({ org_id }: { org_id?: string }) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: shippingLocations, isPending: isLoading } = useQuery({
    queryKey: [queryKeys.shippingLocations, org_id],
    queryFn: async () => {
      let query = supabase.from('shipping_locations').select('*')

      if (org_id) {
        query = query.eq('org_id', org_id)
      }

      const { data, error } = await query.order('created_at', {
        ascending: false,
      })

      if (error) throw error
      return data
    },
    enabled: !!org_id,
  })

  const createShippingLocation = useMutation({
    mutationFn: async (data: InsertShippingLocation) =>
      await supabase
        .from('shipping_locations')
        .insert(data)
        .select('id')
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.shippingLocations],
      })
      toast({
        variant: 'success',
        title: 'Shipping location created',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to create shipping location',
        description: error.message,
      })
    },
  })

  const updateShippingLocation = useMutation({
    mutationFn: async (updateData: UpdateShippingLocation) =>
      await supabase
        .from('shipping_locations')
        .update(updateData)
        .eq('id', updateData.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.shippingLocations],
      })
      toast({
        variant: 'success',
        title: 'Shipping location updated',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to update shipping location',
        description: error.message,
      })
    },
  })

  const deleteShippingLocation = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('shipping_locations').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.shippingLocations],
      })
      toast({
        variant: 'success',
        title: 'Shipping location deleted',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to delete shipping location',
        description: error.message,
      })
    },
  })

  return {
    shippingLocations,
    isLoading,
    createShippingLocation,
    updateShippingLocation,
    deleteShippingLocation,
  }
}
